// ======================================================
// REHBER360 — AKILLI PLAN ROBOTU
// PLAN VALIDATOR
// ======================================================
//
// Bu katman TAMAMLANMIŞ TAKVİMİ denetler.
//
// Kontrol edilen başlıca durumlar:
//
// - Günlük dakika HARD LIMIT
// - Ağır görev sınırı
// - Günlük maksimum görev sayısı
// - Yanlış sınav / alan dersi
// - Kullanıcının seçmediği ders
// - Müfredatta olmayan konu
// - Seçilmeyen konu
// - Tamamlanmış konunun yeniden "yeni konu" olması
// - Deneme + yeni konu çakışması
// - Aynı gün birden fazla deneme
// - Slotların kaybolması / iki kez yerleştirilmesi
// - Yeni konu sırasının bozulması
// - Buffer dağılımı
// - Aşırı ders yığılması
//
// Validator:
// - Plan üretmez
// - Slot taşımaz
// - Takvimi değiştirmez
//
// Sadece rapor üretir.
// ======================================================




import {
  getMufredatVerisi
} from '../../data/coaching/coachingCatalog';

import {
  SMART_PLAN_TASK_TYPES,
  SMART_PLAN_TOPIC_STATUS,
  SMART_PLAN_LIMITS,
  isSubjectAllowed,
  isTopicInCurriculum,
  validateDailyLoad,
  calculateDailyMinutes,
  getWeeklyBufferSlotCount
} from './smartPlanRules';


// ======================================================
// 1. DESTEKLENEN GÖREV TÜRLERİ
// ======================================================

const VALID_TASK_TYPES =
  new Set(
    Object.values(
      SMART_PLAN_TASK_TYPES
    )
  );


// ======================================================
// 2. DERS GEREKTİREN GÖREVLER
// ======================================================

const SUBJECT_REQUIRED_TYPES =
  new Set([
    SMART_PLAN_TASK_TYPES.NEW_TOPIC,
    SMART_PLAN_TASK_TYPES.PRACTICE,
    SMART_PLAN_TASK_TYPES.REVIEW,
    SMART_PLAN_TASK_TYPES.MIXED_PRACTICE,
    SMART_PLAN_TASK_TYPES.BRANCH_MOCK
  ]);


// ======================================================
// 3. KONU GEREKTİREN GÖREVLER
// ======================================================

const TOPIC_REQUIRED_TYPES =
  new Set([
    SMART_PLAN_TASK_TYPES.NEW_TOPIC,
    SMART_PLAN_TASK_TYPES.PRACTICE,
    SMART_PLAN_TASK_TYPES.REVIEW
  ]);


// ======================================================
// 4. DENEME TÜRLERİ
// ======================================================

const ASSESSMENT_TYPES =
  new Set([
    SMART_PLAN_TASK_TYPES.FULL_MOCK,
    SMART_PLAN_TASK_TYPES.BRANCH_MOCK,
    SMART_PLAN_TASK_TYPES.TOPIC_SCAN,
    SMART_PLAN_TASK_TYPES.WEEKLY_SCAN
  ]);


// ======================================================
// 5. PROGRAMI DÜZ GÜN LİSTESİNE ÇEVİR
// ======================================================

export const flattenSmartPlanSchedule = schedule => {

  if (!Array.isArray(schedule)) {
    return [];
  }

  return schedule.flatMap(
    week =>
      Array.isArray(week?.days)
        ? week.days
        : []
  );
};


// ======================================================
// 6. PROGRAMDAKİ TÜM GÖREVLER
// ======================================================

export const flattenSmartPlanTasks = schedule => {

  return flattenSmartPlanSchedule(
    schedule
  ).flatMap(day =>
    Array.isArray(day?.tasks)
      ? day.tasks
      : []
  );
};


// ======================================================
// 7. KONU DURUM ANAHTARI
// ======================================================

const getTopicStatus = ({
  subject,
  topic,
  topicStatuses = {}
}) => {

  if (
    !subject ||
    !topic
  ) {
    return null;
  }

  const key =
    `${subject}__${topic}`;

  return (
    topicStatuses[key] ||
    SMART_PLAN_TOPIC_STATUS.NOT_STARTED
  );
};


// ======================================================
// 8. GÜNDE DENEME SAYISI
// ======================================================

const countAssessmentsOnDay = tasks => {

  if (!Array.isArray(tasks)) {
    return 0;
  }

  return tasks.filter(task =>
    ASSESSMENT_TYPES.has(
      task?.type
    )
  ).length;
};


// ======================================================
// 9. GÜNDE YENİ KONU VAR MI?
// ======================================================

const hasNewTopicOnDay = tasks => {

  if (!Array.isArray(tasks)) {
    return false;
  }

  return tasks.some(
    task =>
      task?.type ===
      SMART_PLAN_TASK_TYPES.NEW_TOPIC
  );
};


// ======================================================
// 10. GÜNDE DENEME VAR MI?
// ======================================================

const hasAssessmentOnDay = tasks => {

  return (
    countAssessmentsOnDay(tasks) > 0
  );
};


// ======================================================
// 11. AYNI ID KAÇ KEZ VAR?
// ======================================================

const findDuplicateIds = tasks => {

  const counts = {};

  tasks.forEach(task => {

    if (!task?.id) {
      return;
    }

    counts[task.id] =
      (counts[task.id] || 0) + 1;
  });

  return Object.entries(counts)
    .filter(
      ([, count]) =>
        count > 1
    )
    .map(
      ([id, count]) => ({
        id,
        count
      })
    );
};


// ======================================================
// 12. TAKVİM YAPISI KONTROLÜ
// ======================================================

const validateScheduleStructure = ({
  schedule,
  config
}) => {

  const errors = [];

  if (!Array.isArray(schedule)) {

    return [
      {
        code:
          'INVALID_SCHEDULE_STRUCTURE'
      }
    ];
  }


  const expectedWeeks =
    Number(config?.weeks || 0);

  const expectedDays =
    Array.isArray(
      config?.workingDays
    )
      ? config.workingDays
      : [];


  if (
    schedule.length !==
    expectedWeeks
  ) {

    errors.push({
      code:
        'WEEK_COUNT_MISMATCH',

      expected:
        expectedWeeks,

      actual:
        schedule.length
    });
  }


  schedule.forEach(
    (week, weekIndex) => {

      const expectedWeekNumber =
        weekIndex + 1;


      if (
        Number(
          week?.weekNumber
        ) !==
        expectedWeekNumber
      ) {

        errors.push({
          code:
            'INVALID_WEEK_NUMBER',

          expected:
            expectedWeekNumber,

          actual:
            week?.weekNumber
        });
      }


      const days =
        Array.isArray(week?.days)
          ? week.days
          : [];


      if (
        days.length !==
        expectedDays.length
      ) {

        errors.push({
          code:
            'WORKING_DAY_COUNT_MISMATCH',

          weekNumber:
            expectedWeekNumber,

          expected:
            expectedDays.length,

          actual:
            days.length
        });
      }


      days.forEach(
        (day, dayIndex) => {

          const expectedDay =
            expectedDays[
              dayIndex
            ];


          if (
            day?.day !==
            expectedDay
          ) {

            errors.push({
              code:
                'WORKING_DAY_ORDER_MISMATCH',

              weekNumber:
                expectedWeekNumber,

              position:
                dayIndex,

              expected:
                expectedDay,

              actual:
                day?.day
            });
          }


          if (
            !Array.isArray(
              day?.tasks
            )
          ) {

            errors.push({
              code:
                'INVALID_DAY_TASKS',

              weekNumber:
                expectedWeekNumber,

              day:
                day?.day
            });
          }
        }
      );
    }
  );


  return errors;
};


// ======================================================
// 13. TEK GÖREVİ DOĞRULA
// ======================================================

const validateTask = ({
  task,
  config,
  weekNumber,
  day
}) => {

  const errors = [];
  const warnings = [];


  // ----------------------------------------------------
  // ID
  // ----------------------------------------------------

  if (!task?.id) {

    errors.push({
      code:
        'TASK_ID_REQUIRED',

      weekNumber,
      day
    });
  }


  // ----------------------------------------------------
  // GÖREV TÜRÜ
  // ----------------------------------------------------

  if (
    !VALID_TASK_TYPES.has(
      task?.type
    )
  ) {

    errors.push({
      code:
        'INVALID_TASK_TYPE',

      taskId:
        task?.id,

      type:
        task?.type,

      weekNumber,
      day
    });


    return {
      errors,
      warnings
    };
  }


  // ----------------------------------------------------
  // SÜRE
  // ----------------------------------------------------

  const duration =
    Number(task?.duration);


  if (
    !Number.isFinite(duration) ||
    duration <= 0
  ) {

    errors.push({
      code:
        'INVALID_TASK_DURATION',

      taskId:
        task?.id,

      duration:
        task?.duration,

      weekNumber,
      day
    });
  }


  // ----------------------------------------------------
  // DERS ZORUNLULUĞU
  // ----------------------------------------------------

  if (
    SUBJECT_REQUIRED_TYPES.has(
      task.type
    ) &&
    !task.subject
  ) {

    errors.push({
      code:
        'TASK_SUBJECT_REQUIRED',

      taskId:
        task.id,

      type:
        task.type,

      weekNumber,
      day
    });
  }


  // ----------------------------------------------------
  // KONU ZORUNLULUĞU
  // ----------------------------------------------------

  if (
    TOPIC_REQUIRED_TYPES.has(
      task.type
    ) &&
    !task.topic
  ) {

    errors.push({
      code:
        'TASK_TOPIC_REQUIRED',

      taskId:
        task.id,

      type:
        task.type,

      weekNumber,
      day
    });
  }


  // ----------------------------------------------------
  // DERS KONTROLLERİ
  // ----------------------------------------------------

  if (task.subject) {

    const selectedSubjects =
      Array.isArray(
        config?.selectedSubjects
      )
        ? config.selectedSubjects
        : [];


    if (
      !selectedSubjects.includes(
        task.subject
      )
    ) {

      errors.push({
        code:
          'UNSELECTED_SUBJECT_IN_PLAN',

        taskId:
          task.id,

        subject:
          task.subject,

        weekNumber,
        day
      });
    }


    if (
      !isSubjectAllowed({
        subject:
          task.subject,

        examMode:
          config?.examMode,

        field:
          config?.field
      })
    ) {

      errors.push({
        code:
          'SUBJECT_NOT_ALLOWED_FOR_EXAM',

        taskId:
          task.id,

        subject:
          task.subject,

        weekNumber,
        day
      });
    }
  }


  // ----------------------------------------------------
  // KONU KONTROLLERİ
  // ----------------------------------------------------

  if (
    task.subject &&
    task.topic
  ) {

    if (
  !isTopicInCurriculum({
    subject:
      task.subject,

    topic:
      task.topic,

    curriculumModel:
      config?.curriculumModel
  })
) {

      errors.push({
        code:
          'TOPIC_NOT_FOUND_IN_CURRICULUM',

        taskId:
          task.id,

        subject:
          task.subject,

        topic:
          task.topic,

        weekNumber,
        day
      });
    }


    const explicitTopics =
      config?.selectedTopics
        ?.[task.subject];


    if (
      Array.isArray(
        explicitTopics
      ) &&
      explicitTopics.length > 0 &&
      !explicitTopics.includes(
        task.topic
      )
    ) {

      errors.push({
        code:
          'UNSELECTED_TOPIC_IN_PLAN',

        taskId:
          task.id,

        subject:
          task.subject,

        topic:
          task.topic,

        weekNumber,
        day
      });
    }


    // --------------------------------------------------
    // TAMAMLANMIŞ KONU YENİ KONU OLAMAZ
    // --------------------------------------------------

    if (
      task.type ===
        SMART_PLAN_TASK_TYPES.NEW_TOPIC
    ) {

      const status =
        getTopicStatus({
          subject:
            task.subject,

          topic:
            task.topic,

          topicStatuses:
            config?.topicStatuses || {}
        });


      if (
        status ===
        SMART_PLAN_TOPIC_STATUS.COMPLETED
      ) {

        errors.push({
          code:
            'COMPLETED_TOPIC_USED_AS_NEW',

          taskId:
            task.id,

          subject:
            task.subject,

          topic:
            task.topic,

          weekNumber,
          day
        });
      }
    }
  }



    // ----------------------------------------------------
  // SCOPED ASSESSMENT — METADATA + KONU KAPSAMI
  // ----------------------------------------------------
  //
  // TOPIC_SCAN / WEEKLY_SCAN kendi gerçek ölçüm
  // kapsamını assessmentTopics metadata'sında taşır.
  //
  // Bu nedenle önce metadata yapısının güvenilir
  // olduğunu doğrularız.
  //
  // Ardından mevcut scope containment kontrolü çalışır.
  // ----------------------------------------------------

  const isScopedAssessment =
    task?.type ===
      SMART_PLAN_TASK_TYPES.TOPIC_SCAN ||
    task?.type ===
      SMART_PLAN_TASK_TYPES.WEEKLY_SCAN;


  if (
    isScopedAssessment
  ) {

    const assessmentTopics =
      Array.isArray(
        task?.assessmentTopics
      )
        ? task.assessmentTopics
        : [];


    // ----------------------------------------------
    // A. EN AZ BİR GERÇEK KONU OLMALI
    // ----------------------------------------------

    if (
      assessmentTopics.length === 0
    ) {

      errors.push({
        code:
          'SCOPED_ASSESSMENT_TOPICS_REQUIRED',

        taskId:
          task?.id,

        assessmentType:
          task?.type,

        weekNumber,
        day
      });
    }


    // ----------------------------------------------
    // B. DECLARED COUNT GERÇEK ARRAY İLE UYUŞMALI
    // ----------------------------------------------

    const declaredAssessmentTopicCount =
      Number(
        task?.assessmentTopicCount
      );


    if (
      !Number.isInteger(
        declaredAssessmentTopicCount
      ) ||
      declaredAssessmentTopicCount !==
        assessmentTopics.length
    ) {

      errors.push({
        code:
          'SCOPED_ASSESSMENT_TOPIC_COUNT_MISMATCH',

        taskId:
          task?.id,

        assessmentType:
          task?.type,

        declared:
          Number.isFinite(
            declaredAssessmentTopicCount
          )
            ? declaredAssessmentTopicCount
            : null,

        actual:
          assessmentTopics.length,

        weekNumber,
        day
      });
    }


    const selectedSubjects =
      Array.isArray(
        config?.selectedSubjects
      )
        ? config.selectedSubjects
        : [];


    assessmentTopics.forEach(
      (assessmentTopic, topicIndex) => {

        const subject =
          assessmentTopic?.subject;

        const topic =
          assessmentTopic?.topic;


        // ------------------------------------------
        // C. HER SATIR GEÇERLİ SUBJECT/TOPIC TAŞIMALI
        // ------------------------------------------

        const subjectValid =
          typeof subject ===
            'string' &&
          subject.trim().length > 0;


        const topicValid =
          typeof topic ===
            'string' &&
          topic.trim().length > 0;


        if (
          !subjectValid ||
          !topicValid
        ) {

          errors.push({
            code:
              'SCOPED_ASSESSMENT_TOPIC_INVALID',

            taskId:
              task?.id,

            assessmentType:
              task?.type,

            topicIndex,

            subject:
              subject ?? null,

            topic:
              topic ?? null,

            subjectValid,

            topicValid,

            weekNumber,
            day
          });

          // Bozuk satır scope containment'a sokulmaz.
          return;
        }


        // ------------------------------------------
        // D. SEÇİLİ KAPSAM KORUMASI
        // ------------------------------------------

        const subjectSelected =
          selectedSubjects.includes(
            subject
          );


        const explicitTopics =
          config?.selectedTopics
            ?.[subject];


        const topicSelected =
          !Array.isArray(
            explicitTopics
          ) ||
          explicitTopics.length === 0 ||
          explicitTopics.includes(
            topic
          );


        if (
          !subjectSelected ||
          !topicSelected
        ) {

          errors.push({
            code:
              'SCOPED_ASSESSMENT_TOPIC_OUT_OF_SCOPE',

            taskId:
              task?.id,

            assessmentType:
              task?.type,

            subject,

            topic,

            subjectSelected,

            topicSelected,

            weekNumber,
            day
          });
        }
      }
    );
  }


  // ----------------------------------------------------
  // SCHEDULED FLAG
  // ----------------------------------------------------

  if (
    task.scheduled === false
  ) {

    warnings.push({
      code:
        'SCHEDULED_TASK_MARKED_FALSE',

      taskId:
        task.id,

      weekNumber,
      day
    });
  }


  return {
    errors,
    warnings
  };
};


// ======================================================
// 14. GÜNLÜK KURALLARI DOĞRULA
// ======================================================

const validateDay = ({
  day,
  config
}) => {

  const errors = [];
  const warnings = [];

  const tasks =
    Array.isArray(day?.tasks)
      ? day.tasks
      : [];


  // ----------------------------------------------------
  // GÜNLÜK YÜK
  // ----------------------------------------------------

  const loadValidation =
    validateDailyLoad({
      tasks,

      dailyMinutes:
        config?.dailyMinutes,

      strategyId:
        config?.strategyId
    });


  loadValidation.violations
    .forEach(violation => {

      errors.push({
        ...violation,

        weekNumber:
          day?.weekNumber,

        day:
          day?.day
      });
    });


  // ----------------------------------------------------
  // GÜNLÜK GÖREV SAYISI
  // ----------------------------------------------------

  if (
    tasks.length >
    SMART_PLAN_LIMITS
      .maxTasksPerDay
  ) {

    errors.push({
      code:
        'DAILY_TASK_LIMIT_EXCEEDED',

      weekNumber:
        day?.weekNumber,

      day:
        day?.day,

      current:
        tasks.length,

      limit:
        SMART_PLAN_LIMITS
          .maxTasksPerDay
    });
  }


  // ----------------------------------------------------
  // BİRDEN FAZLA DENEME
  // ----------------------------------------------------

  const assessmentCount =
    countAssessmentsOnDay(
      tasks
    );


  if (
    assessmentCount > 1
  ) {

    errors.push({
      code:
        'MULTIPLE_ASSESSMENTS_SAME_DAY',

      weekNumber:
        day?.weekNumber,

      day:
        day?.day,

      count:
        assessmentCount
    });
  }


  // ----------------------------------------------------
  // DENEME + YENİ KONU
  // ----------------------------------------------------

  if (
    hasAssessmentOnDay(
      tasks
    ) &&
    hasNewTopicOnDay(
      tasks
    )
  ) {

    errors.push({
      code:
        'ASSESSMENT_AND_NEW_TOPIC_SAME_DAY',

      weekNumber:
        day?.weekNumber,

      day:
        day?.day
    });
  }


  // ----------------------------------------------------
  // AYNI SLOTUN GÜNDE İKİ KEZ OLMASI
  // ----------------------------------------------------

  const duplicates =
    findDuplicateIds(
      tasks
    );


  duplicates.forEach(
    duplicate => {

      errors.push({
        code:
          'DUPLICATE_TASK_ID_ON_DAY',

        weekNumber:
          day?.weekNumber,

        day:
          day?.day,

        taskId:
          duplicate.id,

        count:
          duplicate.count
      });
    }
  );


  // ----------------------------------------------------
  // TEK TEK GÖREVLER
  // ----------------------------------------------------

  tasks.forEach(task => {

    const result =
      validateTask({
        task,

        config,

        weekNumber:
          day?.weekNumber,

        day:
          day?.day
      });


    errors.push(
      ...result.errors
    );

    warnings.push(
      ...result.warnings
    );
  });


  return {
    errors,
    warnings
  };
};


// ======================================================
// 15. SLOT KAPSAMA KONTROLÜ
// ======================================================
//
// Kaynak slotlardan biri:
//
// - Ya scheduled olmalı
// - Ya unscheduled listesinde bulunmalı
//
// Sessizce kaybolamaz.
// ======================================================

const validateSlotCoverage = ({
  sourceSlots = [],
  scheduledTasks = [],
  unscheduledSlots = []
}) => {

  const errors = [];


  if (
    !Array.isArray(
      sourceSlots
    ) ||
    sourceSlots.length === 0
  ) {
    return errors;
  }


  const sourceIds =
    new Set(
      sourceSlots
        .map(slot => slot?.id)
        .filter(Boolean)
    );


  const scheduledIds =
    new Set(
      scheduledTasks
        .map(slot => slot?.id)
        .filter(Boolean)
    );


  const unscheduledIds =
    new Set(
      unscheduledSlots
        .map(slot => slot?.id)
        .filter(Boolean)
    );


  // ----------------------------------------------------
  // KAYBOLAN SLOT
  // ----------------------------------------------------

  sourceIds.forEach(id => {

    if (
      !scheduledIds.has(id) &&
      !unscheduledIds.has(id)
    ) {

      errors.push({
        code:
          'SOURCE_SLOT_LOST',

        taskId:
          id
      });
    }
  });


  // ----------------------------------------------------
  // HEM SCHEDULED HEM UNSCHEDULED
  // ----------------------------------------------------

  sourceIds.forEach(id => {

    if (
      scheduledIds.has(id) &&
      unscheduledIds.has(id)
    ) {

      errors.push({
        code:
          'SLOT_BOTH_SCHEDULED_AND_UNSCHEDULED',

        taskId:
          id
      });
    }
  });


  // ----------------------------------------------------
  // KAYNAKTA OLMAYAN SCHEDULED SLOT
  // ----------------------------------------------------

  scheduledIds.forEach(id => {

    if (
      !sourceIds.has(id)
    ) {

      errors.push({
        code:
          'UNKNOWN_SCHEDULED_SLOT',

        taskId:
          id
      });
    }
  });


  return errors;
};


// ======================================================
// 16. GLOBAL DUPLICATE SLOT KONTROLÜ
// ======================================================

const validateGlobalDuplicates =
  scheduledTasks => {

    const errors = [];

    const duplicates =
      findDuplicateIds(
        scheduledTasks
      );


    duplicates.forEach(
      duplicate => {

        errors.push({
          code:
            'DUPLICATE_SCHEDULED_SLOT',

          taskId:
            duplicate.id,

          count:
            duplicate.count
        });
      }
    );


    return errors;
  };


// ======================================================
// 17. YENİ KONU SIRASI KONTROLÜ
// ======================================================
//
// Slot Builder konu sırasını belirler.
// Scheduler bu sırayı bozabilir ama değiştiremez.
//
// Aynı dersin yeni konuları takvimde,
// kaynak slot sırasıyla ilerlemelidir.
// ======================================================

const validateNewTopicOrder = ({
  sourceSlots = [],
  scheduledTasks = []
}) => {

  const errors = [];


  const scheduledIdSet =
    new Set(
      scheduledTasks
        .map(task => task?.id)
        .filter(Boolean)
    );


  const sourceBySubject = {};


  sourceSlots
    .filter(
      slot =>
        slot?.type ===
        SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
        slot?.subject &&
        scheduledIdSet.has(
          slot.id
        )
    )
    .forEach(slot => {

      if (
        !sourceBySubject[
          slot.subject
        ]
      ) {
        sourceBySubject[
          slot.subject
        ] = [];
      }

      sourceBySubject[
        slot.subject
      ].push(
        slot.id
      );
    });


  const actualBySubject = {};


  [...scheduledTasks]
    .filter(
      task =>
        task?.type ===
        SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
        task?.subject
    )
    .sort((a, b) => {

      const dayDiff =
        Number(
          a.globalDayIndex || 0
        ) -
        Number(
          b.globalDayIndex || 0
        );


      if (dayDiff !== 0) {
        return dayDiff;
      }


      return (
        Number(
          a.scheduleOrder || 0
        ) -
        Number(
          b.scheduleOrder || 0
        )
      );
    })
    .forEach(task => {

      if (
        !actualBySubject[
          task.subject
        ]
      ) {
        actualBySubject[
          task.subject
        ] = [];
      }

      actualBySubject[
        task.subject
      ].push(
        task.id
      );
    });


  Object.entries(
    sourceBySubject
  ).forEach(
    ([subject, expectedIds]) => {

      const actualIds =
        actualBySubject[
          subject
        ] || [];


      const sameLength =
        expectedIds.length ===
        actualIds.length;


      const sameOrder =
        sameLength &&
        expectedIds.every(
          (id, index) =>
            actualIds[index] === id
        );


      if (!sameOrder) {

        errors.push({
          code:
            'NEW_TOPIC_ORDER_VIOLATION',

          subject,

          expected:
            expectedIds,

          actual:
            actualIds
        });
      }
    }
  );


  return errors;
};


// ======================================================
// 18. BUFFER DAĞILIMI
// ======================================================
//
// Yeterli buffer slot üretilmişse,
// stratejinin istediği haftalık dağılım korunmalı.
//
// Kapasite yüzünden daha az buffer üretildiyse
// bunu hard error yapmıyoruz.
// ======================================================

const validateBufferDistribution = ({
  schedule,
  sourceSlots,
  config
}) => {

  const warnings = [];


  const weeklyTarget =
    getWeeklyBufferSlotCount(
      config?.strategyId
    );


  if (
    weeklyTarget <= 0
  ) {
    return warnings;
  }


  const totalWeeks =
    Number(
      config?.weeks || 0
    );


  const sourceBufferCount =
    sourceSlots.filter(
      slot =>
        slot?.type ===
        SMART_PLAN_TASK_TYPES.BUFFER
    ).length;


  const fullExpectedCount =
    totalWeeks *
    weeklyTarget;


  // Yeterli slot zaten üretilmemişse
  // haftalık hedef kontrolü yapmayız.
  if (
    sourceBufferCount <
    fullExpectedCount
  ) {
    return warnings;
  }


  schedule.forEach(week => {

    const count =
      week.days.reduce(
        (total, day) =>
          total +
          day.tasks.filter(
            task =>
              task?.type ===
              SMART_PLAN_TASK_TYPES.BUFFER
          ).length,
        0
      );


    if (
      count <
      weeklyTarget
    ) {

      warnings.push({
        code:
          'BUFFER_TARGET_NOT_MET',

        weekNumber:
          week.weekNumber,

        expected:
          weeklyTarget,

        actual:
          count
      });
    }
  });


  return warnings;
};


// ======================================================
// 19. DERS YIĞILMASI KONTROLÜ
// ======================================================
//
// Bu HARD ERROR değildir.
//
// Aynı ders 3 çalışma günü üst üste görünürse
// koça uyarı verilir.
//
// Tek ders seçilmiş planlarda doğal olarak bu
// kaçınılmaz olduğundan kontrol yapılmaz.
// ======================================================

const validateSubjectClustering = ({
  schedule,
  config
}) => {

  const warnings = [];


  const selectedSubjects =
    Array.isArray(
      config?.selectedSubjects
    )
      ? config.selectedSubjects
      : [];


  if (
    selectedSubjects.length <= 1
  ) {
    return warnings;
  }


  const days =
    flattenSmartPlanSchedule(
      schedule
    );


  selectedSubjects.forEach(
    subject => {

      let streak = 0;

      let streakStart = null;


      days.forEach(
        (day, index) => {

          const containsSubject =
            day.tasks.some(
              task =>
                task?.subject ===
                subject
            );


          if (containsSubject) {

            if (streak === 0) {
              streakStart =
                index;
            }

            streak += 1;


            if (streak === 3) {

              warnings.push({
                code:
                  'SUBJECT_CLUSTERED_CONSECUTIVE_DAYS',

                subject,

                startDayIndex:
                  streakStart,

                endDayIndex:
                  index
              });
            }
          }

          else {

            streak = 0;

            streakStart = null;
          }
        }
      );
    }
  );


  return warnings;
};


// ======================================================
// 20. DENEME ANALİZİ YAKINLIK KONTROLÜ
// ======================================================
//
// Tam deneme varsa ve analiz slotu da mevcutsa,
// analiz mümkünse aynı gün veya sonraki çalışma
// gününde bulunmalı.
//
// Bu soft warning'dir.
// ======================================================

const validateMockAnalysisProximity = ({
  scheduledTasks
}) => {

  const warnings = [];


  const mocks =
    scheduledTasks
      .filter(
        task =>
          task?.type ===
          SMART_PLAN_TASK_TYPES.FULL_MOCK
      )
      .sort(
        (a, b) =>
          Number(
            a.globalDayIndex || 0
          ) -
          Number(
            b.globalDayIndex || 0
          )
      );


  const analyses =
    scheduledTasks
      .filter(
        task =>
          task?.type ===
          SMART_PLAN_TASK_TYPES.MOCK_ANALYSIS
      )
      .sort(
        (a, b) =>
          Number(
            a.globalDayIndex || 0
          ) -
          Number(
            b.globalDayIndex || 0
          )
      );


  const pairCount =
    Math.min(
      mocks.length,
      analyses.length
    );


  for (
    let index = 0;
    index < pairCount;
    index += 1
  ) {

    const mock =
      mocks[index];

    const analysis =
      analyses[index];


    const distance =
      Number(
        analysis.globalDayIndex || 0
      ) -
      Number(
        mock.globalDayIndex || 0
      );


    if (
      distance < 0 ||
      distance > 1
    ) {

      warnings.push({
        code:
          'MOCK_ANALYSIS_TOO_FAR',

        mockId:
          mock.id,

        analysisId:
          analysis.id,

        dayDistance:
          distance
      });
    }
  }


  return warnings;
};


// ======================================================
// 21. FINAL SCHEDULE — KONU KAPSAMA METRİĞİ
// ======================================================
//
// Validator, SlotBuilder'ın coverage warning'ine
// güvenmez.
//
// Final schedule üzerinden bağımsız olarak:
//
// - hedef NOT_STARTED konuları,
// - gerçekten NEW_TOPIC olarak schedule edilenleri,
// - eksik kalanları
//
// tekrar hesaplar.
//
// Eksik kapsam HARD ERROR değildir.
// Bu fonksiyon yalnız metrik üretir.
// ======================================================

const calculateTopicCoverageMetrics = ({
  scheduledTasks = [],
  config
}) => {

  const selectedSubjects =
    Array.isArray(
      config?.selectedSubjects
    )
      ? config.selectedSubjects
      : [];


  const targetTopicKeys =
    new Set();


  // ----------------------------------------------------
  // HEDEF NOT_STARTED KAPSAMI
  // ----------------------------------------------------

  const curriculumData =
  getMufredatVerisi(
    config?.curriculumModel
  );


selectedSubjects.forEach(
  subject => {

    const curriculum =
      Array.isArray(
        curriculumData?.[subject]
      )
        ? curriculumData[subject]
        : [];


    const explicitTopics =
      config?.selectedTopics
        ?.[subject];


      const effectiveTopics =
        (
          Array.isArray(
            explicitTopics
          ) &&
          explicitTopics.length > 0
        )
          ? explicitTopics.filter(
              topic =>
                curriculum.includes(
                  topic
                )
            )
          : curriculum;


      effectiveTopics.forEach(
        topic => {

          const status =
            getTopicStatus({
              subject,
              topic,

              topicStatuses:
                config?.topicStatuses ||
                {}
            });


          if (
            status !==
              SMART_PLAN_TOPIC_STATUS
                .NOT_STARTED
          ) {

            return;
          }


          targetTopicKeys.add(
            `${subject}__${topic}`
          );
        }
      );
    }
  );


  // ----------------------------------------------------
  // FINAL SCHEDULE'DAKİ NEW_TOPIC KAPSAMI
  // ----------------------------------------------------

  const scheduledNewTopicKeys =
    new Set(
      scheduledTasks
        .filter(
          task =>
            task?.type ===
              SMART_PLAN_TASK_TYPES
                .NEW_TOPIC &&
            task?.subject &&
            task?.topic
        )
        .map(
          task =>
            `${task.subject}__${task.topic}`
        )
    );


  // ----------------------------------------------------
  // EKSİK KAPSAM
  // ----------------------------------------------------

  const missingTopics =
    [
      ...targetTopicKeys
    ]
      .filter(
        topicKey =>
          !scheduledNewTopicKeys
            .has(
              topicKey
            )
      );


  const targetTopicCount =
    targetTopicKeys.size;


  const missingTopicCount =
    missingTopics.length;


  const coveredTopicCount =
    targetTopicCount -
    missingTopicCount;


  const coveragePercent =
    targetTopicCount > 0
      ? Math.round(
          (
            coveredTopicCount /
            targetTopicCount
          ) *
          100
        )
      : 100;


  return {

    targetTopicCount,

    coveredTopicCount,

    missingTopicCount,

    coveragePercent,

    complete:
      missingTopicCount === 0,

    missingTopics
  };
};




// ======================================================
// 20B. SCOPED ASSESSMENT EXPOSURE SAFETY
// ======================================================
//
// NOT_STARTED bir konu:
//
// NEW_TOPIC
//      ↓
// PRACTICE
//      ↓
// TOPIC_SCAN / WEEKLY_SCAN
//
// sırasındaki minimum öğrenme zeminini görmeden
// scoped assessment kapsamına giremez.
//
// Bu kontrol:
//
// - final schedule üzerinden çalışır
// - source slot'a güvenmez
// - yalnız NOT_STARTED konular için zorunludur
// - COMPLETED / INCOMPLETE konuların geçmişte zaten
//   öğrenilmiş olabileceğini kabul eder
//
// ======================================================


// ----------------------------------------------------
// TAKVİMDE A GÖREVİ B'DEN ÖNCE Mİ?
// ----------------------------------------------------

const isScheduledTaskBefore = (
  earlierTask,
  laterTask
) => {

  const earlierDay =
    Number(
      earlierTask?.globalDayIndex
    );

  const laterDay =
    Number(
      laterTask?.globalDayIndex
    );


  if (
    !Number.isFinite(
      earlierDay
    ) ||
    !Number.isFinite(
      laterDay
    )
  ) {
    return false;
  }


  // Farklı günlerdeyse doğrudan gün sırası yeterlidir.
  if (
    earlierDay !==
    laterDay
  ) {

    return (
      earlierDay <
      laterDay
    );
  }


  // Aynı gündeyse scheduleOrder belirleyicidir.
  const earlierOrder =
    Number(
      earlierTask?.scheduleOrder
    );

  const laterOrder =
    Number(
      laterTask?.scheduleOrder
    );


  if (
    !Number.isFinite(
      earlierOrder
    ) ||
    !Number.isFinite(
      laterOrder
    )
  ) {
    return false;
  }


  return (
    earlierOrder <
    laterOrder
  );
};


// ----------------------------------------------------
// SCOPED ASSESSMENT EXPOSURE KONTROLÜ
// ----------------------------------------------------

const validateScopedAssessmentExposure = ({
  scheduledTasks = [],
  config
}) => {

  const errors = [];


  const scopedAssessments =
    scheduledTasks.filter(
      task =>
        task?.type ===
          SMART_PLAN_TASK_TYPES.TOPIC_SCAN ||
        task?.type ===
          SMART_PLAN_TASK_TYPES.WEEKLY_SCAN
    );


  scopedAssessments.forEach(
    assessmentTask => {

      const assessmentTopics =
        Array.isArray(
          assessmentTask?.assessmentTopics
        )
          ? assessmentTask
              .assessmentTopics
          : [];


      assessmentTopics.forEach(
        assessmentTopic => {

          const subject =
            assessmentTopic?.subject;

          const topic =
            assessmentTopic?.topic;


          if (
            !subject ||
            !topic
          ) {
            return;
          }


          // ------------------------------------------
          // YALNIZ NOT_STARTED İÇİN ZORUNLU
          // ------------------------------------------

          const topicStatus =
            getTopicStatus({
              subject,
              topic,

              topicStatuses:
                config?.topicStatuses ||
                {}
            });


          if (
            topicStatus !==
              SMART_PLAN_TOPIC_STATUS
                .NOT_STARTED
          ) {
            return;
          }


          // ------------------------------------------
          // ASSESSMENT'TAN ÖNCEKİ NEW_TOPIC'LAR
          // ------------------------------------------

          const priorNewTopics =
            scheduledTasks.filter(
              task =>

                task?.type ===
                  SMART_PLAN_TASK_TYPES
                    .NEW_TOPIC &&

                task?.subject ===
                  subject &&

                task?.topic ===
                  topic &&

                isScheduledTaskBefore(
                  task,
                  assessmentTask
                )
            );


          // ------------------------------------------
          // ASSESSMENT'TAN ÖNCEKİ PRACTICE'LAR
          // ------------------------------------------

          const priorPractices =
            scheduledTasks.filter(
              task =>

                task?.type ===
                  SMART_PLAN_TASK_TYPES
                    .PRACTICE &&

                task?.subject ===
                  subject &&

                task?.topic ===
                  topic &&

                isScheduledTaskBefore(
                  task,
                  assessmentTask
                )
            );


          // ------------------------------------------
          // GEÇERLİ PRACTICE:
          //
          // yalnız assessment'tan önce olması yetmez.
          //
          // Ondan önce gerçek NEW_TOPIC da bulunmalıdır.
          //
          // Böylece:
          //
          // PRACTICE
          // NEW_TOPIC
          // ASSESSMENT
          //
          // gibi ters bir zincir geçerli sayılamaz.
          // ------------------------------------------

          const validPractice =
            priorPractices.find(
              practiceTask =>

                priorNewTopics.some(
                  newTopicTask =>
                    isScheduledTaskBefore(
                      newTopicTask,
                      practiceTask
                    )
                )
            ) ||
            null;


          const hasNewTopicBeforeAssessment =
            priorNewTopics.length > 0;


          const hasPracticeAfterNewTopic =
            Boolean(
              validPractice
            );


          if (
            !hasNewTopicBeforeAssessment ||
            !hasPracticeAfterNewTopic
          ) {

            errors.push({
              code:
                'SCOPED_ASSESSMENT_EXPOSURE_REQUIRED',

              taskId:
                assessmentTask?.id,

              assessmentType:
                assessmentTask?.type,

              subject,

              topic,

              topicStatus,

              hasNewTopicBeforeAssessment,

              hasPracticeAfterNewTopic,

              weekNumber:
                assessmentTask
                  ?.weekNumber,

              day:
                assessmentTask
                  ?.day
            });
          }
        }
      );
    }
  );


  return errors;
};


// ======================================================
// 21. PROGRAM DAKİKA ÖZETİ
// ======================================================





const calculateScheduleMetrics = ({
  schedule,
  unscheduledSlots = [],
  config
}) => {

  const days =
    flattenSmartPlanSchedule(
      schedule
    );


  const tasks =
    flattenSmartPlanTasks(
      schedule
    );


  const scheduledMinutes =
    tasks.reduce(
      (total, task) =>
        total +
        Number(
          task?.duration || 0
        ),
      0
    );


  const unscheduledMinutes =
    unscheduledSlots.reduce(
      (total, task) =>
        total +
        Number(
          task?.duration || 0
        ),
      0
    );


  const usedDays =
  days.filter(
    day =>
      Array.isArray(
        day?.tasks
      ) &&
      day.tasks.length > 0
  ).length;


const topicCoverage =
  calculateTopicCoverageMetrics({
    scheduledTasks:
      tasks,

    config
  });


return {
    totalDays:
      days.length,

    usedDays,

    totalTasks:
      tasks.length,

    scheduledMinutes,

unscheduledMinutes,

topicCoverage
  };
};


// ======================================================
// 22. ANA VALIDATOR
// ======================================================
//
// Kullanım:
//
// const validation = validateSmartPlanSchedule({
//   scheduleResult,
//   config,
//   sourceSlots: slotResult.slots
// });
//
// ======================================================

export const validateSmartPlanSchedule = ({
  scheduleResult,
  config,
  sourceSlots = []
}) => {

  const errors = [];
  const warnings = [];


  // ----------------------------------------------------
  // GİRDİ
  // ----------------------------------------------------

  if (
    !scheduleResult ||
    !config
  ) {

    return {
      valid: false,

      errors: [
        {
          code:
            'VALIDATOR_INPUT_REQUIRED'
        }
      ],

      warnings,

      metrics: null
    };
  }


  const schedule =
    Array.isArray(
      scheduleResult.schedule
    )
      ? scheduleResult.schedule
      : [];


  const unscheduledSlots =
    Array.isArray(
      scheduleResult
        .unscheduledSlots
    )
      ? scheduleResult
          .unscheduledSlots
      : [];


  // ----------------------------------------------------
  // TAKVİM YAPISI
  // ----------------------------------------------------

  errors.push(
    ...validateScheduleStructure({
      schedule,
      config
    })
  );


  // ----------------------------------------------------
  // GÜNLER
  // ----------------------------------------------------

  const days =
    flattenSmartPlanSchedule(
      schedule
    );


  days.forEach(day => {

    const result =
      validateDay({
        day,
        config
      });


    errors.push(
      ...result.errors
    );

    warnings.push(
      ...result.warnings
    );
  });


  // ----------------------------------------------------
  // TÜM SCHEDULED GÖREVLER
  // ----------------------------------------------------

  const scheduledTasks =
    flattenSmartPlanTasks(
      schedule
    );


  // ----------------------------------------------------
  // SCOPED ASSESSMENT EXPOSURE SAFETY
  // ----------------------------------------------------

  errors.push(
    ...validateScopedAssessmentExposure({
      scheduledTasks,
      config
    })
  );


  // ----------------------------------------------------
  // GLOBAL DUPLICATE
  // ----------------------------------------------------

  errors.push(
    ...validateGlobalDuplicates(
      scheduledTasks
    )
  );


  // ----------------------------------------------------
  // SOURCE SLOT KAPSAMA
  // ----------------------------------------------------

  errors.push(
    ...validateSlotCoverage({
      sourceSlots,
      scheduledTasks,
      unscheduledSlots
    })
  );


  // ----------------------------------------------------
  // YENİ KONU SIRASI
  // ----------------------------------------------------

  errors.push(
    ...validateNewTopicOrder({
      sourceSlots,
      scheduledTasks
    })
  );


  // ----------------------------------------------------
  // UNSCHEDULED SLOT = PLAN TAM DEĞİL
  // ----------------------------------------------------

  if (
    unscheduledSlots.length > 0
  ) {

    errors.push({
      code:
        'PLAN_HAS_UNSCHEDULED_SLOTS',

      count:
        unscheduledSlots.length,

      slotIds:
        unscheduledSlots.map(
          slot =>
            slot?.id
        )
    });
  }


  // ----------------------------------------------------
  // BUFFER DAĞILIMI
  // ----------------------------------------------------

  warnings.push(
    ...validateBufferDistribution({
      schedule,
      sourceSlots,
      config
    })
  );


  // ----------------------------------------------------
  // DERS YIĞILMASI
  // ----------------------------------------------------

  warnings.push(
    ...validateSubjectClustering({
      schedule,
      config
    })
  );


  // ----------------------------------------------------
  // DENEME ANALİZİ
  // ----------------------------------------------------

  warnings.push(
    ...validateMockAnalysisProximity({
      scheduledTasks
    })
  );


  // ----------------------------------------------------
  // BOŞ PLAN
  // ----------------------------------------------------

  if (
    scheduledTasks.length === 0 &&
    sourceSlots.length > 0
  ) {

    errors.push({
      code:
        'NO_TASKS_SCHEDULED'
    });
  }


  // ----------------------------------------------------
  // ÖZET
  // ----------------------------------------------------

 const metrics =
  calculateScheduleMetrics({
    schedule,
    unscheduledSlots,
    config
  });


  return {
    valid:
      errors.length === 0,

    errors,

    warnings,

    metrics
  };
};